import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/PageHeader";
import { HSE_PILLARS } from "../data/site";

export default function SafetyPage({ onQuoteClick }) {
  return (
    <div data-testid="safety-page">
      <Seo title="Safety &amp; HSE — IDEAL ENTERPRISES" description="Our HSE culture, audits, and safety pillars across every site, every shift." />
      <PageHeader
        overline="HSE / SAFETY"
        title="Safety is not a policy. It's how we hold a hammer."
        subtitle="A safety-first culture, daily audits, and an incident-reporting practice that rewards transparency."
        bgImage="https://images.unsplash.com/photo-1587582423116-ec07293f0395"
      />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="overline">Our HSE Practice</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-blue mt-3">
              Zero compromise. Every site. Every shift.
            </h2>
            <p className="text-slate-700 mt-6 leading-relaxed">
              We operate every site under a documented HSE Plan aligned with OISD, PNGRB T4S, and client safety standards. Our HSE officers are NEBOSH / IOSH-certified, and every supervisor is empowered to stop work if a single condition deviates from the JSA.
            </p>
            <p className="text-slate-700 mt-4 leading-relaxed">
              In 2024 we delivered <span className="text-brand-orange font-semibold">zero lost-time incidents</span> across more than 1.2 million man-hours worked. That number isn't luck — it's the dividend of relentless practice.
            </p>
          </div>
          <div className="lg:col-span-6 aspect-[4/3] overflow-hidden border-2 border-brand-orange/30">
            <img src="https://images.pexels.com/photos/8961146/pexels-photo-8961146.jpeg" alt="Engineers reviewing blueprints" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F8F9FA] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="overline text-center">Pillars of our HSE Programme</p>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-blue mt-3 text-center max-w-3xl mx-auto">
            Seven pillars, lived every day on every site.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 grid-borders border-t border-l border-slate-200 bg-white">
            {HSE_PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
                className="p-8 group hover:bg-slate-50 transition-colors"
                data-testid={`hse-pillar-${i}`}
              >
                <ShieldCheck size={26} className="text-brand-orange" />
                <h3 className="font-heading font-bold text-xl text-brand-blue mt-5">{p.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-orange">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-heading font-black text-3xl md:text-4xl text-white">
            Safety records you can audit.
          </h2>
          <p className="text-white/90 mt-4 max-w-2xl mx-auto">
            Prospective clients are welcome to inspect our HSE policy, incident registers, and TBT logs as part of due diligence.
          </p>
          <button
            onClick={onQuoteClick}
            data-testid="safety-page-quote"
            className="mt-8 bg-brand-blue text-white px-8 py-4 font-semibold btn-lift hover:bg-brand-blue-dark"
          >
            Talk to our HSE team
          </button>
        </div>
      </section>
    </div>
  );
}
